import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { AppServices } from 'middleware'
import { PosPaymentMethod, NewPosPaymentMethod, BulkUpdateItem } from './paymentMethodsService'
import { createQueryResponseHandling } from 'utils/errorHandler'
import { showErrorMessage, showSuccessMessage } from 'uiComponents/message'
import { t } from 'translations'

const getErrorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof Error && error.message) {
        return error.message
    }
    return fallback
}

export const posPaymentMethodsApi = createApi({
    reducerPath: 'posPaymentMethodsApi',
    tagTypes: ['posPaymentMethods'],
    baseQuery: fetchBaseQuery({}),
    endpoints: (builder) => ({}),
})

const extendedApi = posPaymentMethodsApi.injectEndpoints({
    endpoints: (builder) => ({
        getPosPaymentMethods: builder.query<PosPaymentMethod[], string>({
            providesTags: ['posPaymentMethods'],
            queryFn: async (accountSlug) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.posPaymentMethodsService.getPaymentMethods(accountSlug),
                )
                if (response.error) {
                    showErrorMessage(
                        getErrorMessage(
                            response.error,
                            t('settings.accountSettings.paymentMethods.pos.errors.loadFailed'),
                        ),
                    )
                }
                return response
            },
        }),
        createPosPaymentMethod: builder.mutation<
            PosPaymentMethod,
            { accountSlug: string; data: NewPosPaymentMethod }
        >({
            invalidatesTags: ['posPaymentMethods'],
            queryFn: async ({ accountSlug, data }) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.posPaymentMethodsService.createPaymentMethod(accountSlug, data),
                )
                if (response.error) {
                    showErrorMessage(
                        getErrorMessage(
                            response.error,
                            t('settings.accountSettings.paymentMethods.pos.errors.createFailed'),
                        ),
                    )
                } else {
                    showSuccessMessage(t('settings.accountSettings.paymentMethods.pos.createSuccess'))
                }
                return response
            },
        }),
        updatePosPaymentMethod: builder.mutation<
            PosPaymentMethod,
            { accountSlug: string; id: number; data: Partial<PosPaymentMethod> }
        >({
            invalidatesTags: ['posPaymentMethods'],
            queryFn: async ({ accountSlug, id, data }) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.posPaymentMethodsService.updatePaymentMethod(accountSlug, id, data),
                )
                if (response.error) {
                    showErrorMessage(
                        getErrorMessage(
                            response.error,
                            t('settings.accountSettings.paymentMethods.pos.errors.updateFailed'),
                        ),
                    )
                }
                return response
            },
        }),
        deletePosPaymentMethod: builder.mutation<void, { accountSlug: string; id: number }>({
            invalidatesTags: ['posPaymentMethods'],
            queryFn: async ({ accountSlug, id }) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.posPaymentMethodsService.deletePaymentMethod(accountSlug, id),
                )
                if (response.error) {
                    showErrorMessage(
                        getErrorMessage(
                            response.error,
                            t('settings.accountSettings.paymentMethods.pos.errors.deleteFailed'),
                        ),
                    )
                } else {
                    showSuccessMessage(t('settings.accountSettings.paymentMethods.pos.deleteSuccess'))
                }
                return response
            },
        }),
        bulkUpdatePosPaymentMethods: builder.mutation<void, { accountSlug: string; items: BulkUpdateItem[] }>({
            invalidatesTags: ['posPaymentMethods'],
            queryFn: async ({ accountSlug, items }) => {
                const response = await createQueryResponseHandling(() =>
                    AppServices.posPaymentMethodsService.bulkUpdate(accountSlug, items),
                )
                if (response.error) {
                    showErrorMessage(
                        getErrorMessage(
                            response.error,
                            t('settings.accountSettings.paymentMethods.pos.errors.reorderFailed'),
                        ),
                    )
                }
                return response
            },
        }),
    }),
})

export const { reducer, reducerPath } = posPaymentMethodsApi
export const {
    useGetPosPaymentMethodsQuery,
    useCreatePosPaymentMethodMutation,
    useUpdatePosPaymentMethodMutation,
    useDeletePosPaymentMethodMutation,
    useBulkUpdatePosPaymentMethodsMutation,
    util: posPaymentMethodsUtil,
} = extendedApi
